import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import SearchDropdown from "./SearchDropdown";
import ProfileDropdown from "./ProfileDropdown";
import { useCart } from "../context/CartContext";

export default function Navbar() {
  const [searchOpen, setSearchOpen]   = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen]   = useState(false);
  const navigate    = useNavigate();
  const { cartCount } = useCart();
  const isLoggedIn  = !!localStorage.getItem("token");
  const searchRef   = useRef(null);
  const profileRef  = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Smart Categories click:
  // • On home page → smooth scroll to #categories section
  // • On any other page → navigate home then jump to section
  const handleCategoriesClick = () => {
    if (window.location.pathname === "/") {
      document.getElementById("categories")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        document.getElementById("categories")?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  };

  const handleCustomizeClick = () => {
    if (window.location.pathname === "/") {
      document.getElementById("customize")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        document.getElementById("customize")?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  };

  const linkStyle = {
    color: "#444",
    fontFamily: "Georgia, serif",
    letterSpacing: "0.12em",
    fontSize: "12px",
    textTransform: "uppercase",
    textDecoration: "none",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    transition: "color 0.2s",
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 py-4"
      style={{
        background: "rgba(255,255,255,0.97)",
        borderBottom: "1px solid #f0ebe3",
        backdropFilter: "blur(8px)",
        fontFamily: "Georgia, serif",
      }}
    >
      {/* ── Logo ──────────────────────────────────────────────────────── */}
      <Link to="/" className="flex items-center gap-2 no-underline" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
        <img
          src="/logo.jpeg"
          alt="SJ Aroma Candles"
          style={{ height: "42px", width: "42px", objectFit: "contain", borderRadius: "50%" }}
        />
        <span className="font-bold" style={{ fontSize: "14px", color: "#1a1a1a", letterSpacing: "0.12em", fontFamily: "Georgia, serif" }}>
          Aroma Candles
        </span>
      </Link>

      {/* ── Desktop nav links ──────────────────────────────────────────── */}
      <div className="hidden md:flex items-center gap-10">
        {/* Shop All */}
        <Link
          to="/products"
          style={linkStyle}
          onMouseEnter={(e) => (e.target.style.color = "#e07b39")}
          onMouseLeave={(e) => (e.target.style.color = "#444")}
        >
          Shop All
        </Link>

        {/* Categories — smart scroll, NOT a <Link> */}
        <button
          onClick={handleCategoriesClick}
          style={linkStyle}
          onMouseEnter={(e) => (e.target.style.color = "#e07b39")}
          onMouseLeave={(e) => (e.target.style.color = "#444")}
        >
          Categories
        </button>

        {/* Customize */}
        <button
          onClick={handleCustomizeClick}
          style={linkStyle}
          onMouseEnter={(e) => (e.target.style.color = "#e07b39")}
          onMouseLeave={(e) => (e.target.style.color = "#444")}
        >
          Customize
        </button>

        {/* Our Story */}
        <Link
          to="/about"
          style={linkStyle}
          onMouseEnter={(e) => (e.target.style.color = "#e07b39")}
          onMouseLeave={(e) => (e.target.style.color = "#444")}
        >
          Our Story
        </Link>
      </div>

      {/* ── Right icons ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-5">

        {/* Search */}
        <div className="relative" ref={searchRef}>
          <button
            onClick={() => { setSearchOpen(!searchOpen); setProfileOpen(false); }}
            style={{ color: searchOpen ? "#e07b39" : "#444", background: "none", border: "none", cursor: "pointer", padding: "4px" }}
          >
            <SearchIcon />
          </button>
          {searchOpen && <SearchDropdown onClose={() => setSearchOpen(false)} />}
        </div>

        {/* Profile */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => {
              if (!isLoggedIn) { navigate("/login"); return; }
              setProfileOpen(!profileOpen);
              setSearchOpen(false);
            }}
            style={{ color: profileOpen ? "#e07b39" : "#444", background: "none", border: "none", cursor: "pointer", padding: "4px" }}
          >
            <ProfileIcon />
          </button>
          {profileOpen && isLoggedIn && <ProfileDropdown onClose={() => setProfileOpen(false)} />}
        </div>

        {/* Cart — only when logged in */}
        {isLoggedIn && (
          <button
            onClick={() => navigate("/cart")}
            className="relative"
            style={{ color: "#444", background: "none", border: "none", cursor: "pointer", padding: "4px" }}
          >
            <CartIcon />
            {cartCount > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 text-white rounded-full flex items-center justify-center font-bold"
                style={{ fontSize: "9px", minWidth: "16px", height: "16px", background: "#e07b39", fontFamily: "Georgia, serif" }}
              >
                {cartCount}
              </span>
            )}
          </button>
        )}

        {/* Mobile toggle */}
        <button
          className="md:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ color: "#444", background: "none", border: "none", cursor: "pointer", fontSize: "20px" }}
        >
          {mobileOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* ── Mobile menu ────────────────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="absolute top-full left-0 right-0 flex flex-col py-4"
          style={{ background: "rgba(255,255,255,0.98)", borderBottom: "1px solid #f0ebe3" }}
        >
          <Link to="/products" className="px-8 py-3 text-xs uppercase no-underline"
            style={{ color: "#444", fontFamily: "Georgia, serif", letterSpacing: "0.1em" }}
            onClick={() => setMobileOpen(false)}>
            Shop All
          </Link>

          <button
            onClick={() => { setMobileOpen(false); handleCategoriesClick(); }}
            className="px-8 py-3 text-xs uppercase text-left"
            style={{ color: "#444", fontFamily: "Georgia, serif", letterSpacing: "0.1em", background: "none", border: "none", cursor: "pointer" }}
          >
            Categories
          </button>

          <button
            onClick={() => { setMobileOpen(false); handleCustomizeClick(); }}
            className="px-8 py-3 text-xs uppercase text-left"
            style={{ color: "#444", fontFamily: "Georgia, serif", letterSpacing: "0.1em", background: "none", border: "none", cursor: "pointer" }}
          >
            Customize
          </button>

          <Link to="/about" className="px-8 py-3 text-xs uppercase no-underline"
            style={{ color: "#444", fontFamily: "Georgia, serif", letterSpacing: "0.1em" }}
            onClick={() => setMobileOpen(false)}>
            Our Story
          </Link>
        </div>
      )}
    </nav>
  );
}

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const ProfileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const CartIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);